/**
 * `<page-subject-bindings>` — the subject-binding list, routed at
 * `/instances/:id/subject-bindings`.
 *
 * `SubjectBinding` is one of the eleven policy kinds and it gets its own surface
 * anyway, because what an operator comes here to do is a **credential lifecycle**
 * rather than a policy edit: this is where instance API keys are minted, shown once
 * and revoked (`SC-F521`). Folding it into the generic policy editor would put a key
 * lifecycle behind a kind picker.
 *
 * Implements the list half of `SC-F520` / `SC-F521`, and the mode asymmetry of
 * `03` §2.4.8.1 as a column: which credential a binding accepts decides which
 * template paths resolve for every policy that references its subjects
 * (`SC-F513`), so it belongs on the row rather than one click in.
 *
 * The four states are all here: loading (a table skeleton), error (the `apierr` code
 * and message with the `request_id` a reader quotes), the two distinguishable zero
 * states of `SC-S907` — nothing authored yet, versus everything filtered out — and
 * populated.
 *
 * Stories: `SC-S518`, `SC-S519`, `SC-S521`.
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import type {
  BnColumn,
  BnRow,
  BnRowClickDetail,
  BnSelectChangeDetail,
  BnSelectOption,
} from "../components/index.js";

import { ApiError, parseError } from "../api/errors.js";
import { getInstance, type Instance } from "../api/instances.js";
import { getPolicy, listPolicies, type PolicyObject } from "../api/policies.js";
import { hrefFor, navigate } from "../nav.js";

import { bindingMode, modeLabel, summarize } from "./policy/bindings.js";
import type { SubjectBindingSpec } from "./policy/editor-subjectbinding.js";

/**
 * One row of this list.
 *
 * `BindingSummary` is deliberately narrower — it carries what the row-filter
 * template picker needs and nothing else — so the row keeps the whole record beside
 * it and reuses `summarize` only for the credential mode. Deriving the mode twice is
 * how two surfaces come to disagree about which template paths resolve.
 */
interface BindingRow {
  object: PolicyObject;
  spec: SubjectBindingSpec;
  mode: "jwt" | "api-key" | "both" | "none";
  modeLabel: string;
}

const COLUMNS: BnColumn[] = [
  { key: "name", label: "Binding" },
  { key: "mode", label: "Accepts", width: "1px" },
  { key: "identity", label: "Identity source", mono: true },
  { key: "keys", label: "Keys", width: "1px", align: "right" },
  { key: "limits", label: "Limits", width: "1px" },
  { key: "findings", label: "Findings", width: "1px" },
];

/** The mode filter. `""` is "any". The three values are the derived modes of
 * `bindings.ts`, not a vocabulary of this page's own. */
const MODE_OPTIONS: BnSelectOption[] = [
  { value: "", label: "Any credential" },
  { value: "jwt", label: "JWT" },
  { value: "api-key", label: "API keys" },
  { value: "both", label: "Both" },
  {
    value: "none",
    label: "Neither",
    description: "A binding that authenticates nobody — usually an unfinished edit.",
  },
];

/** Keystroke debounce on the search box, so typing a name is one query. */
const SEARCH_DEBOUNCE_MS = 300;

@customElement("page-subject-bindings")
export class PageSubjectBindings extends LitElement {
  @property() instanceId = "";

  @state() private rows: BindingRow[] = [];
  @state() private loading = true;
  @state() private error: ApiError | null = null;
  @state() private instance: Instance | null = null;

  @state() private q = "";
  @state() private mode = "";

  private searchTimer = 0;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    header {
      display: flex;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    .head-text {
      flex: 1 1 24rem;
      min-width: 0;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    .lede {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      max-width: 70ch;
    }
    .toolbar {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-md);
    }
    .toolbar bn-field {
      margin-bottom: 0;
    }
    .search {
      flex: 1 1 var(--bino-search-width);
      max-width: var(--bino-search-width-focus);
    }
    .filter {
      width: 12rem;
    }
    .cell-name {
      color: var(--bino-primary);
      font-weight: 600;
      text-decoration: none;
    }
    .cell-name:hover {
      text-decoration: underline;
    }
    .cell-name:focus-visible {
      outline: none;
      border-radius: var(--bino-radius-sm);
      box-shadow: var(--bino-focus-ring-shadow);
    }
    .sub {
      display: block;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .meta {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .error-message {
      margin: 0 0 var(--bino-space-md);
      max-width: 70ch;
    }
    .error-actions {
      display: flex;
      gap: var(--bino-space-sm);
      margin-top: var(--bino-space-md);
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    .notes {
      display: grid;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-md);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    void this.load();
    void this.loadInstance();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.searchTimer);
  }

  /** The instance record is a convenience: losing it costs the subtitle, not the
   * page, so its failure is deliberately not the page's error state. */
  private async loadInstance() {
    if (this.instanceId === "") return;
    try {
      this.instance = await getInstance(this.instanceId);
    } catch {
      this.instance = null;
    }
  }

  /**
   * Read every binding in the draft.
   *
   * The list route answers rows without specs, and a binding's row needs its spec —
   * the issuer, the key count, the claim mapping. So the page reads the objects and
   * summarizes them, which is what `bindings.ts` exists for: it is the same summary
   * the row-filter template picker consumes, so the two never disagree about what mode
   * a binding is in.
   */
  private async load() {
    if (this.instanceId === "") return;
    this.loading = true;
    try {
      const page = await listPolicies(this.instanceId, {
        kind: "SubjectBinding",
        q: this.q || undefined,
        limit: 200,
      });
      const objects = await Promise.all(page.items.map((row) => this.readObject(row.name)));
      this.rows = objects
        .filter((o): o is PolicyObject => o !== null)
        .map((o) => {
          const summary = summarize(o);
          return {
            object: o,
            spec: (o.spec ?? {}) as SubjectBindingSpec,
            mode: bindingMode(summary),
            modeLabel: modeLabel(summary),
          };
        });
      this.error = null;
    } catch (err) {
      this.error = parseError(err);
      this.rows = [];
    } finally {
      this.loading = false;
    }
  }

  /** One object read, tolerating a single failure: a binding this build cannot decode
   * must not blank the whole list, because the list is how a reader reaches it to
   * delete it. */
  private async readObject(name: string): Promise<PolicyObject | null> {
    try {
      return await getPolicy(this.instanceId, "SubjectBinding", name);
    } catch {
      return null;
    }
  }

  private onSearch(e: CustomEvent<{ value: string }>) {
    this.q = e.detail.value;
    clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(() => void this.load(), SEARCH_DEBOUNCE_MS);
  }

  private get filtered(): BindingRow[] {
    if (this.mode === "") return this.rows;
    return this.rows.filter((b) => b.mode === this.mode);
  }

  private get isFiltered(): boolean {
    return this.q !== "" || this.mode !== "";
  }

  private toRows(): BnRow[] {
    return this.filtered.map((row) => {
      const keys = row.spec.apiKeys ?? [];
      const missingHashes = keys.filter((k) => !k.hashRef).length;
      const name = row.object.metadata.name;
      return {
        id: name,
        name: html`<a
            class="cell-name"
            href=${hrefFor("instances", this.instanceId, "subject-bindings", name)}
            >${name}</a
          >
          ${row.spec.claims?.subjectId
            ? html`<span class="sub">subject id from ${row.spec.claims.subjectId}</span>`
            : nothing}
          ${row.object.unreadable
            ? html`<span class="sub">this build cannot decode the stored spec</span>`
            : nothing}`,
        mode: html`<bn-badge .variant=${row.mode === "none" ? "warning" : "info"}>
          ${row.modeLabel}
        </bn-badge>`,
        identity: row.spec.issuer || (keys.length > 0 ? "API keys only" : "—"),
        keys:
          keys.length === 0
            ? html`<span class="sub">none</span>`
            : missingHashes > 0
              ? html`${keys.length}<span class="sub">${missingHashes} without a hash</span>`
              : String(keys.length),
        limits: this.renderLimits(row),
        findings: this.renderFindings(row),
      };
    });
  }

  private renderLimits(row: BindingRow) {
    const parts: string[] = [];
    if (row.spec.rateLimit?.rps) parts.push(`${row.spec.rateLimit.rps}/s`);
    if (row.spec.budget?.rowsPerDay) parts.push(`${row.spec.budget.rowsPerDay} rows/day`);
    if (row.spec.budget?.cpuSecondsPerDay) {
      parts.push(`${row.spec.budget.cpuSecondsPerDay} cpu-s/day`);
    }
    return parts.length === 0 ? html`<span class="sub">none</span>` : html`${parts.join(" · ")}`;
  }

  private renderFindings(row: BindingRow) {
    const findings = row.object.findings ?? [];
    const errors = findings.filter((f) => f.severity === "error").length;
    const warnings = findings.length - errors;
    if (errors === 0 && warnings === 0) return html`<span class="sub">clean</span>`;
    return html`${errors > 0
      ? html`<bn-badge variant="error">${errors} blocking</bn-badge>`
      : nothing}${warnings > 0
      ? html`<bn-badge variant="warning">${warnings} warning</bn-badge>`
      : nothing}`;
  }

  /** Notes that appear only when a row on screen needs them. Each one is a failure
   * mode that is invisible in the row itself. */
  private renderNotes() {
    const notes = [];
    if (this.filtered.some((r) => (r.spec.apiKeys ?? []).some((k) => !k.hashRef))) {
      notes.push(html`<bn-panel eyebrow="A key without a hash is a silent partial outage">
        <p class="meta">
          Sluice <strong>skips</strong> a key whose <code>hashRef</code> fails to resolve, with only a
          warning — the binding keeps working for its other keys, so one integration stops
          authenticating and nothing looks broken. The publish gate refuses a generation that carries
          one.
        </p>
      </bn-panel>`);
    }
    if (this.filtered.some((r) => r.mode === "none")) {
      notes.push(html`<bn-panel eyebrow="A binding that authenticates nobody">
        <p class="meta">
          A binding with no issuer, no shared secret and no keys decodes and resolves no subject. It is
          usually an unfinished edit rather than a mistake, and it costs nothing at runtime — but no
          policy will ever match a subject through it.
        </p>
      </bn-panel>`);
    }
    const issuers = new Map<string, string[]>();
    for (const row of this.rows) {
      const issuer = row.spec.issuer;
      if (!issuer) continue;
      issuers.set(issuer, [...(issuers.get(issuer) ?? []), row.object.metadata.name]);
    }
    const duplicates = [...issuers.entries()].filter(([, names]) => names.length > 1);
    if (duplicates.length > 0) {
      notes.push(html`<bn-panel eyebrow="Duplicate issuer — publish is blocked">
        <p class="meta">
          Sluice rejects a duplicate non-empty issuer and <strong>keeps the previous binding set
          live</strong>, so the symptom is an instance silently ignoring the new credentials.
          ${duplicates.map(
            ([issuer, names]) => html` <code>${issuer}</code> is declared by ${names.join(", ")}.`,
          )}
        </p>
      </bn-panel>`);
    }
    return notes.length === 0 ? nothing : html`<div class="notes">${notes}</div>`;
  }

  private renderError(err: ApiError) {
    return html`<bn-card heading="Could not load subject bindings">
      <div role="alert">
        <p class="error-message">${err.message}</p>
        <bn-kv
          .rows=${[
            { label: "Error code", value: err.code },
            ...(err.requestId ? [{ label: "Request id", value: err.requestId }] : []),
          ]}
        ></bn-kv>
        ${err.requestId
          ? nothing
          : html`<p class="meta">
              The request did not reach Sluice Cloud, so there is no request id to quote. Check your
              connection and try again.
            </p>`}
        <div class="error-actions">
          <bn-button variant="primary" @click=${() => void this.load()}>Try again</bn-button>
        </div>
      </div>
    </bn-card>`;
  }

  private renderEmpty() {
    if (this.isFiltered) {
      return html`<bn-empty-state
        icon="⌕"
        heading="No bindings match these filters"
        message="This instance may still have bindings — the current search or credential filter excludes all of them."
      >
        <bn-button
          slot="actions"
          @click=${() => {
            this.q = "";
            this.mode = "";
            void this.load();
          }}
        >
          Clear filters
        </bn-button>
      </bn-empty-state>`;
    }
    return html`<bn-empty-state
      icon="⚿"
      heading="No subject bindings yet"
      message="A subject binding turns an inbound credential — a JWT from your identity provider, or an API key this screen mints — into a subject that policies can match on. Without one, every request reaching this instance is anonymous, and an anonymous request matches only a policy that names no subject at all."
    >
      <bn-button
        slot="actions"
        variant="primary"
        @click=${() => navigate(hrefFor("instances", this.instanceId, "subject-bindings", "new"))}
      >
        Create a binding
      </bn-button>
    </bn-empty-state>`;
  }

  override render() {
    const label = this.instance?.display_name || this.instance?.name || "";
    return html`
      <header>
        <div class="head-text">
          <h1>Subject bindings</h1>
          <p class="lede">
            How ${label === "" ? "this instance" : label} turns a credential into a subject. This is
            also where instance API keys are minted — the control plane generates the material, shows
            it once, and stores only its HMAC.
          </p>
        </div>
        <div>
          <bn-button
            variant="primary"
            @click=${() =>
              navigate(hrefFor("instances", this.instanceId, "subject-bindings", "new"))}
          >
            New binding
          </bn-button>
        </div>
      </header>

      <div class="toolbar">
        <bn-field class="search" label="Search bindings">
          <bn-input
            .value=${this.q}
            type="search"
            placeholder="Name, issuer or key id"
            @input=${this.onSearch}
          ></bn-input>
        </bn-field>
        <bn-field class="filter" label="Credential">
          <bn-select
            .options=${MODE_OPTIONS}
            .value=${this.mode}
            @change=${(e: CustomEvent<BnSelectChangeDetail>) => (this.mode = e.detail.value)}
          ></bn-select>
        </bn-field>
      </div>

      ${this.error
        ? this.renderError(this.error)
        : html`
            ${this.renderNotes()}
            <bn-card flush>
              <bn-table
                caption="Subject bindings"
                clickable
                row-key="id"
                .columns=${COLUMNS}
                .rows=${this.toRows()}
                ?loading=${this.loading}
                @row-click=${(e: CustomEvent<BnRowClickDetail>) =>
                  navigate(
                    hrefFor(
                      "instances",
                      this.instanceId,
                      "subject-bindings",
                      String(e.detail.id),
                    ),
                  )}
              >
                <div slot="empty">${this.renderEmpty()}</div>
              </bn-table>
            </bn-card>
          `}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-subject-bindings": PageSubjectBindings;
  }
}
